<script setup>
import {
    onMounted, ref, watch, computed, defineProps,
} from "vue";
import PolicyModal from "./PolicyModal.vue";
import { useBookingSettingsStore } from "../stores/bookingStore";
import { useMainStore } from "../stores/mainStore";

const props = defineProps({
    storage: {
        type: String,
        default: null,
    },
});

const bookingSettingsStore = useBookingSettingsStore();
const mainStore = useMainStore();

const showPolicyModal = ref(false);
const modalTitle = ref("");
const modalText = ref("");

const showModal = (modalType) => {
    switch (modalType) {
    case "refund policy":
        modalText.value = bookingSettingsStore.refundPolicy;
        modalTitle.value = "Refund policy";
        showPolicyModal.value = true;
        break;

    case "terms and conditions":
        modalText.value = bookingSettingsStore.termsConditions;
        modalTitle.value = "Terms and conditions";
        showPolicyModal.value = true;
        break;

    case "resale policy":
        modalText.value = bookingSettingsStore.resalePolicy;
        modalTitle.value = "Resale policy";
        showPolicyModal.value = true;
        break;

    case "privacy policy":
        modalText.value = bookingSettingsStore.privacyPolicy;
        modalTitle.value = "Privacy policy";
        showPolicyModal.value = true;
        break;
    default:
        console.error("Unknown modal type");
        break;
    }
};

onMounted(async () => {
    await bookingSettingsStore.getLocalStorage(props.storage);

    const mainStoreAndBookingSettingsStoreHasBusinessId = mainStore.businessId
        && bookingSettingsStore.businessId;
    const businessIdIsDifferent = mainStore.businessId !== bookingSettingsStore.businessId;

    if (mainStoreAndBookingSettingsStoreHasBusinessId
        && businessIdIsDifferent) {
        bookingSettingsStore.businessId = mainStore.businessId;
        bookingSettingsStore.removeLocalStorage(props.storage);
    }

    if (!bookingSettingsStore.businessId) {
        await mainStore.getData();
        bookingSettingsStore.businessId = mainStore.businessId;
    }

    await bookingSettingsStore.getSetBookingSettingsWithStorage(props.storage);
});

watch(() => mainStore.businessId, async (newValue) => {
    if (newValue && bookingSettingsStore.businessId) {
        bookingSettingsStore.businessId = newValue;
        bookingSettingsStore.removeLocalStorage(props.storage);
        await bookingSettingsStore.getSetBookingSettingsWithStorage(props.storage);
    }
});

watch(() => bookingSettingsStore.shouldReload, async (newValue) => {
    if (newValue) {
        if (!bookingSettingsStore.businessId) {
            await mainStore.getData();
            bookingSettingsStore.businessId = mainStore.businessId;
        }
        await bookingSettingsStore.getSetBookingSettingsWithStorage(props.storage);
    }
});

const isLoading = computed(() => !bookingSettingsStore.errors?.bookingSettingsApiError
    && (!bookingSettingsStore.isLoaded
        || bookingSettingsStore.shouldReload
        || bookingSettingsStore.isFirstActivity === undefined));

</script>
<template>
    <div>
        <h2>Booking settings</h2>
        <div v-if="bookingSettingsStore.errors?.bookingSettingsApiError">
            <div class="error-text" data-test="validation-error">{{
                bookingSettingsStore.errors?.bookingSettingsApiError
            }}
            </div>
        </div>
        <section
            v-if="isLoading"
        >
            <h5 class="my-5 p-5 d-flex flex-grow-1 align-items-center justify-content-center">
                Booking policies are loading…
            </h5>
        </section>
        <section v-else>
            <section v-if="bookingSettingsStore.isFirstActivity !== undefined">
                <!-- Check it is a first activity -->
                <div v-if="bookingSettingsStore.isFirstActivity" class="d-flex flex-column">
                    <div class="mt-3 d-flex flex-column">
                        <label class="show">Refund policy<span class="required-field">*</span></label>
                        <textarea v-model="bookingSettingsStore.refundPolicy" maxlength="10000" class=""
                                  placeholder="Enter a refund policy"
                                  rows="5"></textarea>
                        <div v-if="bookingSettingsStore.errors?.refundPolicy">
                            <div class="error-text" data-test="validation-error">
                                {{ bookingSettingsStore.errors?.refundPolicy }}
                            </div>
                        </div>
                    </div>

                    <div class="mt-3 d-flex flex-column">
                        <label class="show">Terms and conditions<span class="required-field">*</span></label>
                        <textarea v-model="bookingSettingsStore.termsConditions" maxlength="10000" class=""
                                  placeholder="Enter terms and conditions"
                                  rows="5"></textarea>
                        <div v-if="bookingSettingsStore.errors?.termsConditions">
                            <div class="error-text" data-test="validation-error">{{
                                bookingSettingsStore.errors?.termsConditions
                            }}
                            </div>
                        </div>
                    </div>

                    <div class="mt-3 d-flex flex-column">
                        <label class="show">Resale policy<span class="required-field">*</span></label>
                        <textarea v-model="bookingSettingsStore.resalePolicy" maxlength="10000" class=""
                                  placeholder="Enter a resale policy"
                                  rows="5"></textarea>
                        <div v-if="bookingSettingsStore.errors?.resalePolicy">
                            <div class="error-text" data-test="validation-error">
                                {{ bookingSettingsStore.errors?.resalePolicy }}
                            </div>
                        </div>
                    </div>

                    <div class="mt-3 d-flex flex-column">
                        <label class="show">Privacy policy<span class="required-field">*</span></label>
                        <textarea v-model="bookingSettingsStore.privacyPolicy" maxlength="10000" class=""
                                  placeholder="Enter a privacy policy"
                                  rows="5"></textarea>
                        <div v-if="bookingSettingsStore.errors?.privacyPolicy">
                            <div class="error-text" data-test="validation-error">
                                {{ bookingSettingsStore.errors?.privacyPolicy }}
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else>
                    <label class="show">Your booking policies</label>
                    <div @click="showModal('refund policy')" class="d-flex flex-row w-100 mt-2">Use your
                        <u class="text-underline ml-1 cursor-pointer modal-handler">
                            default refund policy
                        </u>
                    </div>
                    <div @click="showModal('terms and conditions')" class="d-flex flex-row w-100 mt-2">Use your
                        <u class="text-underline ml-1 cursor-pointer modal-handler">
                            default terms and conditions
                        </u>
                    </div>
                    <div @click="showModal('resale policy')" class="d-flex flex-row w-100 mt-2">Use your
                        <u class="text-underline ml-1 cursor-pointer modal-handler">
                            default resale policy
                        </u>
                    </div>
                    <div @click="showModal('privacy policy')" class="d-flex flex-row w-100 mt-2">Use your
                        <u class="text-underline ml-1 cursor-pointer modal-handler">
                            default privacy policy
                        </u>
                    </div>
                </div>
            </section>
        </section>

        <PolicyModal
            :showModal="showPolicyModal"
            :title="modalTitle"
            :text="modalText"
            @closeModal="showPolicyModal = false"
        />
    </div>
</template>
